// Create: making what the MFD runs. Configs, for what a CANBOX reads from the car's bus, are made here, from a draft
// kept in this browser; dashboards will be. Each finished file goes to the owner's library, and from there to their
// MFDs.
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { data, problem } from '../../api';
import { useAuth } from '../../auth';
import { agoText, dayText } from '../../lib/words';
import { keys } from '../../queries';
import { Page } from '../../shell/Page';
import { Badge, ButtonLink, Card, ErrorBanner, ListLink, ListSection, Skeleton } from '../../ui';
import { CpuIcon, FileTextIcon, PaletteIcon, PlusIcon, UploadSimpleIcon } from '../../ui/icons';
import { listDrafts } from './config/drafts';
import s from './CreateHub.module.css';

/** How many of the owner's configs the page lists: the rest are in the library. */
const SHOWN = 6;

export function CreateHub() {
  const api = useAuth().api('owners');
  const library = useQuery({ queryKey: keys.library, queryFn: () => data(api.GET('/library')) });
  const drafts = useQuery({ queryKey: ['config-drafts'], queryFn: listDrafts, gcTime: 0 });
  const configs = library.data?.items.filter((i) => i.kind === 'config') ?? [];
  return (
    <Page
      title="Create"
      root
      subtitle="Make what your TUNE MFD runs. What you make goes to your library, and on to your MFDs."
    >
      <div className={s.cards}>
        <Card
          title={
            <span className={s.lead}>
              <CpuIcon size={22} weight="bold" aria-hidden="true" />
              Configs
            </span>
          }
        >
          <p className={s.text}>
            A config tells the MFD's CANBOX what your car sends on its CAN bus: which values, where they are, and how to
            read them.
          </p>
          <div className={s.actions}>
            <ButtonLink to="/create/configs/new" icon={<PlusIcon size={18} weight="bold" />}>
              New config
            </ButtonLink>
            <ButtonLink to="/library?sheet=add" variant="gray" icon={<UploadSimpleIcon size={18} weight="bold" />}>
              Add a config file
            </ButtonLink>
          </div>
        </Card>
        <Card
          title={
            <span className={s.lead}>
              <PaletteIcon size={22} weight="bold" aria-hidden="true" />
              Dashboards
            </span>
          }
          actions={<Badge tone="info">Coming</Badge>}
        >
          <p className={s.text}>
            The dashboard designer is coming to TUNE Analytics. Until then, dashboards come from your MFD and TUNE's
            theme editor.
          </p>
          <div className={s.actions}>
            <ButtonLink to="/create/dashboards" variant="gray">
              About the designer
            </ButtonLink>
            <ButtonLink to="/library?sheet=add" variant="gray" icon={<UploadSimpleIcon size={18} weight="bold" />}>
              Add a dashboard file
            </ButtonLink>
          </div>
        </Card>
      </div>

      {drafts.data && drafts.data.length > 0 && (
        <ListSection
          header="Drafts in this browser"
          footer="Drafts stay in this browser until you delete them, and go when you sign out. What you save is in your library."
        >
          {drafts.data.map((d) => (
            <ListLink
              key={d.id}
              to={`/create/configs/drafts/${d.id}`}
              icon={<FileTextIcon size={20} weight="bold" />}
              label={d.name}
              subtitle={`Changed ${agoText(d.updatedAt)} · ${
                d.itemId ? (d.unsaved ? 'Changes not yet saved' : 'Saved') : 'Not yet in your library'
              }`}
            />
          ))}
        </ListSection>
      )}

      <ListSection
        header="Your configs"
        headerAction={
          configs.length > SHOWN ? (
            <Link to="/library" className={s.more}>
              See all {configs.length}
            </Link>
          ) : undefined
        }
        footer={
          library.isSuccess && configs.length === 0
            ? "Configs you save here, add from a file, or your MFDs send are listed here. TUNE's are in the catalogue."
            : 'Each opens in the config creator as its next version.'
        }
      >
        {library.isPending && <Skeleton lines={2} label="Loading your configs" />}
        {library.isError && <ErrorBanner retry={() => void library.refetch()}>{problem(library.error)}</ErrorBanner>}
        {configs.slice(0, SHOWN).map((item) => (
          <ListLink
            key={item.id}
            to={`/create/configs/${item.id}`}
            icon={<CpuIcon size={20} weight="bold" />}
            label={item.name}
            subtitle={`Version ${item.latest.n} · ${dayText(item.updatedAt)}`}
          />
        ))}
      </ListSection>
    </Page>
  );
}
